/**
 * PPTX — reading, and text editing that does not disturb the rest of the deck
 * (§8 Phase 2, §17).
 *
 * §17 asks for slides shown in order, speaker notes, a thumbnail strip, and
 * animations preserved but not played. The last of those is the one that
 * shapes the design: a viewer that parsed animations in order to rebuild them
 * would drop the ones it did not understand. This handler never parses them at
 * all — a text edit rewrites the one shape's `<p:txBody>` and every other byte
 * of the package is copied as it was, so timings, transitions, SmartArt,
 * charts and embedded media survive by never being touched (§13, §24).
 *
 * Slides are parsed lazily. §11 wants the first slide before the rest, and a
 * 200-slide deck with embedded video should not decode 200 slides to show one.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import {
  hasPart, openPackage, partsWithPrefix, readPart, readPartText, repackPackage,
} from '../ooxml/package.js';
import type { OpcPackage } from '../ooxml/package.js';
import { parseRelationships } from '../docx/document-xml.js';
import {
  applyShapeEdits, parseSlide, readNotes, readPlaceholderRects, readSlideSize,
} from './slide-xml.js';
import type { SlideRect, SlideShape } from './slide-xml.js';

export type {
  SlideParagraph, SlideRect, SlideRun, SlideShape,
} from './slide-xml.js';

const PRESENTATION_PART = 'ppt/presentation.xml';
const MAX_SHAPES_PER_SLIDE = 400;

export interface PptxSlide {
  readonly index: number;
  /** Part path, e.g. `ppt/slides/slide1.xml`. */
  readonly part: string;
  readonly shapes: readonly SlideShape[];
  /** Speaker notes, empty when the slide has none (§17). */
  readonly notes: string;
  /** Title text, for the outline and the thumbnail strip. */
  readonly title: string;
  /** True when this slide is hidden in the deck. */
  readonly hidden: boolean;
}

export interface PptxImage {
  readonly part: string;
  readonly bytes: Uint8Array;
  readonly contentType: string;
}

export interface PptxModel {
  readonly slides: readonly PptxSlide[];
  readonly width: number;
  readonly height: number;
  /** Pending text edits: slide part → shape id → new text. Applied at export. */
  readonly edits: ReadonlyMap<string, ReadonlyMap<string, string>>;
  readonly images: ReadonlyMap<string, PptxImage>;
  readonly hasMacros: boolean;
  readonly hasAnimations: boolean;
  readonly hasEmbeddedMedia: boolean;
  readonly hasCharts: boolean;
}

const IMAGE_TYPES: Readonly<Record<string, string>> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  bmp: 'image/bmp', webp: 'image/webp', tiff: 'image/tiff', tif: 'image/tiff',
  emf: 'image/emf', wmf: 'image/wmf', svg: 'image/svg+xml',
};

/* ------------------------------------------------------------------ *
 * package structure
 * ------------------------------------------------------------------ */

interface SlideRef { readonly part: string; readonly hidden: boolean }

/**
 * Slide parts in presentation order.
 *
 * The order is `<p:sldIdLst>`, not the part names: `slide10.xml` sorts before
 * `slide2.xml`, and a reordered deck keeps its original file names. Sorting by
 * name would show a rearranged presentation in its old order.
 */
function resolveSlides(pkg: OpcPackage): SlideRef[] {
  const presentation = readPartText(pkg, PRESENTATION_PART);
  const rels = parseRelationships(
    hasPart(pkg, 'ppt/_rels/presentation.xml.rels')
      ? readPartText(pkg, 'ppt/_rels/presentation.xml.rels')
      : '',
    'ppt/',
  );

  const slides: SlideRef[] = [];
  for (const match of presentation.matchAll(/<p:sldId\b[^>]*\/?>/g)) {
    const rid = /\br:id="([^"]+)"/.exec(match[0])?.[1];
    const part = rid ? rels.get(rid) : undefined;
    if (part && pkg.entryByName.has(part)) slides.push({ part, hidden: false });
  }

  if (slides.length === 0) {
    // A deck with no id list is unusual but readable; numeric order is the
    // best available guess and is stated as such by being the fallback.
    const parts = pkg.directory.entries
      .map((e) => e.name)
      .filter((n) => /^ppt\/slides\/slide\d+\.xml$/.test(n))
      .sort((a, b) => slideNumber(a) - slideNumber(b));
    for (const part of parts) slides.push({ part, hidden: false });
  }

  return slides.map((slide) => {
    const xml = readPartText(pkg, slide.part);
    return { ...slide, hidden: /<p:sld\b[^>]*\bshow="0"/.test(xml) };
  });
}

function slideNumber(part: string): number {
  return Number(/slide(\d+)\.xml$/.exec(part)?.[1] ?? 0);
}

/** Placeholder boxes a slide inherits, master first then layout. */
function placeholderRectsFor(pkg: OpcPackage, slidePart: string): Map<string, SlideRect> {
  const table = new Map<string, SlideRect>();
  const slideRels = relsFor(pkg, slidePart);
  const layoutPart = [...slideRels.values()].find((t) => t.startsWith('ppt/slideLayouts/'));
  if (!layoutPart || !hasPart(pkg, layoutPart)) return table;

  const layoutRels = relsFor(pkg, layoutPart);
  const masterPart = [...layoutRels.values()].find((t) => t.startsWith('ppt/slideMasters/'));
  if (masterPart && hasPart(pkg, masterPart)) {
    readPlaceholderRects(readPartText(pkg, masterPart), table);
  }
  // The layout goes second so its boxes win over the master's.
  readPlaceholderRects(readPartText(pkg, layoutPart), table);
  return table;
}

function relsFor(pkg: OpcPackage, part: string): Map<string, string> {
  const slash = part.lastIndexOf('/');
  const dir = part.slice(0, slash);
  const relsPart = `${dir}/_rels/${part.slice(slash + 1)}.rels`;
  if (!hasPart(pkg, relsPart)) return new Map();
  return parseRelationships(readPartText(pkg, relsPart), `${dir}/`);
}

function notesFor(pkg: OpcPackage, slidePart: string): string {
  const notesPart = [...relsFor(pkg, slidePart).values()]
    .find((t) => t.startsWith('ppt/notesSlides/'));
  if (!notesPart || !hasPart(pkg, notesPart)) return '';
  try {
    return readNotes(readPartText(pkg, notesPart));
  } catch {
    return '';
  }
}

function titleOf(shapes: readonly SlideShape[]): string {
  const titled = shapes.find((s) =>
    s.kind === 'text' && (s.placeholder === 'title' || s.placeholder === 'ctrTitle'));
  if (titled && titled.kind === 'text' && titled.text.trim()) return titled.text.trim();
  // No title placeholder — the first text on the slide is what a person would
  // call it, which beats "슬라이드 7" for finding something in a strip.
  const firstText = shapes.find((s) => s.kind === 'text' && s.text.trim() !== '');
  return firstText && firstText.kind === 'text' ? firstText.text.trim().split('\n')[0]! : '';
}

function readSlide(pkg: OpcPackage, ref: SlideRef, index: number): PptxSlide {
  const xml = readPartText(pkg, ref.part);
  const parsed = parseSlide(xml, {
    rels: relsFor(pkg, ref.part),
    placeholderRects: placeholderRectsFor(pkg, ref.part),
    maxShapes: MAX_SHAPES_PER_SLIDE,
  });
  return {
    index,
    part: ref.part,
    shapes: parsed.shapes,
    notes: notesFor(pkg, ref.part),
    title: titleOf(parsed.shapes),
    hidden: ref.hidden,
  };
}

function readImages(
  pkg: OpcPackage, slides: readonly PptxSlide[], budgetBytes: number,
): { images: Map<string, PptxImage>; skipped: string[] } {
  const wanted = new Set<string>();
  for (const slide of slides) {
    for (const shape of slide.shapes) {
      if (shape.kind === 'picture' && shape.part) wanted.add(shape.part);
    }
  }

  const images = new Map<string, PptxImage>();
  const skipped: string[] = [];
  let spent = 0;

  for (const part of wanted) {
    const extension = part.slice(part.lastIndexOf('.') + 1).toLowerCase();
    const contentType = IMAGE_TYPES[extension];
    if (!contentType || !hasPart(pkg, part)) { skipped.push(part); continue; }
    try {
      const bytes = readPart(pkg, part);
      if (spent + bytes.length > budgetBytes) { skipped.push(part); continue; }
      spent += bytes.length;
      images.set(part, { part, bytes, contentType });
    } catch {
      skipped.push(part);
    }
  }
  return { images, skipped };
}

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const pptxHandler: FormatHandler<PptxModel> = {
  id: 'pptx',
  formatIds: ['pptx'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    if (!hasPart(pkg, PRESENTATION_PART)) {
      throw new HandlerError('error.damagedOrIncomplete', `missing ${PRESENTATION_PART}`);
    }

    const refs = resolveSlides(pkg);
    if (refs.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'presentation declares no slides');
    }

    // §11: the first slide, and nothing else.
    const size = readSlideSize(readPartText(pkg, PRESENTATION_PART));
    const first = readSlide(pkg, refs[0]!, 0);

    return {
      kind: 'slides',
      unitCount: refs.length,
      unitKey: 'viewer.slide',
      firstContent: { slide: first, width: size.width, height: size.height },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<PptxModel>> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    if (!hasPart(pkg, PRESENTATION_PART)) {
      throw new HandlerError('error.damagedOrIncomplete', `missing ${PRESENTATION_PART}`);
    }

    const presentationXml = readPartText(pkg, PRESENTATION_PART);
    const refs = resolveSlides(pkg);
    if (refs.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'presentation declares no slides');
    }

    const slides: PptxSlide[] = [];
    for (const [index, ref] of refs.entries()) {
      throwIfAborted(ctx.signal);
      slides.push(readSlide(pkg, ref, index));
    }

    const { images, skipped } = readImages(pkg, slides, ctx.limits.maxFileBytes / 4);
    const size = readSlideSize(presentationXml);

    const hasMacros = pkg.directory.entries.some((e) => e.name.endsWith('vbaProject.bin'));
    const hasCharts = partsWithPrefix(pkg, 'ppt/charts/').length > 0;
    const hasEmbeddedMedia = pkg.directory.entries.some((e) =>
      /^ppt\/media\/.*\.(mp4|m4v|mov|avi|wmv|mp3|wav|m4a|webm)$/i.test(e.name));
    const hasAnimations = slides.some((slide) => {
      const xml = readPartText(pkg, slide.part);
      return /<p:timing\b|<p:transition\b/.test(xml);
    });

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (hasMacros) {
      warnings.push({
        messageKey: 'detect.warning.macrosBlocked',
        severity: 'caution',
        detail: 'vbaProject.bin is preserved byte-for-byte on save and never executed (§17, §30).',
      });
    }
    if (hasAnimations) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Animations and slide transitions are preserved on save but not played (§17).',
      });
    }
    if (hasEmbeddedMedia) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: 'Embedded audio and video are preserved but never auto-played (§17, §30).',
      });
    }
    if (hasCharts) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: 'Charts and SmartArt are shown as empty frames in this release; their parts are preserved on save.',
      });
    }
    if (skipped.length > 0) {
      warnings.push({
        messageKey: 'error.featuresUnsupported',
        severity: 'degraded',
        detail: `${skipped.length} pictures could not be decoded for display; they are preserved on save.`,
      });
    }
    if (source.sizeBytes > 50 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large presentation (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        slides,
        width: size.width,
        height: size.height,
        edits: new Map(),
        images,
        hasMacros,
        hasAnimations,
        hasEmbeddedMedia,
        hasCharts,
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: PptxModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const pkg = openPackage(source.bytes);
    const replacements = new Map<string, Uint8Array>();
    const dropped: string[] = [];

    for (const [part, shapeEdits] of model.edits) {
      if (shapeEdits.size === 0) continue;
      // Re-parse from the original bytes so the spans are this XML's own.
      const xml = readPartText(pkg, part);
      const parsed = parseSlide(xml, {
        rels: relsFor(pkg, part),
        placeholderRects: placeholderRectsFor(pkg, part),
        maxShapes: MAX_SHAPES_PER_SLIDE,
      });
      const result = applyShapeEdits(
        xml, parsed.shapes, [...shapeEdits].map(([id, text]) => ({ id, text })),
      );
      if (result.failed.length > 0) {
        throw new HandlerError(
          'error.unableToRead',
          `could not rewrite shapes in ${part}: ${result.failed.join(', ')}`,
        );
      }
      dropped.push(...result.dropped);
      replacements.set(part, new TextEncoder().encode(result.xml));
    }

    const repacked = repackPackage(pkg, replacements);

    return {
      bytes: repacked.bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: PptxModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const pkg = openPackage(bytes);
      const refs = resolveSlides(pkg);
      reopened = true;

      if (refs.length !== model.slides.length) {
        issues.push(`slide count changed: ${model.slides.length} → ${refs.length}`);
      }

      for (const [index, expected] of model.slides.entries()) {
        const ref = refs[index];
        if (!ref) { issues.push(`slide ${index + 1} disappeared`); continue; }
        if (ref.part !== expected.part) {
          issues.push(`slide ${index + 1} order changed: ${expected.part} → ${ref.part}`);
        }
        const actual = readSlide(pkg, ref, index);
        const edits = model.edits.get(expected.part);

        for (const shape of expected.shapes) {
          const twin = actual.shapes.find((s) => s.id === shape.id);
          if (!twin) { issues.push(`shape ${shape.id} disappeared from slide ${index + 1}`); continue; }
          if (shape.kind !== 'text' || twin.kind !== 'text') continue;
          const edited = edits?.get(shape.id);
          const want = edited ?? shape.originalText;
          if (twin.text !== want) {
            issues.push(`slide ${index + 1} shape ${shape.id}: ${JSON.stringify(want)} → ${JSON.stringify(twin.text)}`);
          }
        }
        if (actual.notes !== expected.notes) {
          issues.push(`slide ${index + 1} speaker notes changed`);
        }
      }

      if (model.hasMacros && !pkg.directory.entries.some((e) => e.name.endsWith('vbaProject.bin'))) {
        issues.push('macro storage was lost during save');
      }
      if (model.hasCharts && partsWithPrefix(pkg, 'ppt/charts/').length === 0) {
        issues.push('chart parts were lost during save');
      }
      for (const part of model.images.keys()) {
        if (!hasPart(pkg, part)) issues.push(`picture part missing: ${part}`);
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported presentation: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/* ------------------------------------------------------------------ *
 * editing helpers
 * ------------------------------------------------------------------ */

/**
 * Queue a shape's new text. Nothing is written until export (§13).
 *
 * Refuses shapes the save path cannot rebuild — pictures, tables, charts, and
 * text boxes holding a generated field. §19: the affordance must not exist
 * where the implementation cannot honour it.
 */
export function setShapeText(
  model: PptxModel, slideIndex: number, shapeId: string, text: string,
): PptxModel {
  const slide = model.slides[slideIndex];
  if (!slide) throw new HandlerError('error.unableToRead', `no slide at index ${slideIndex}`);
  const shape = slide.shapes.find((s) => s.id === shapeId);
  if (!shape) throw new HandlerError('error.unableToRead', `no shape ${shapeId} on slide ${slideIndex + 1}`);
  if (shape.kind !== 'text' || !shape.editable) {
    throw new HandlerError(
      'error.featuresUnsupported',
      `shape ${shapeId} contains content that text editing cannot preserve`,
    );
  }

  const edits = new Map(model.edits);
  const forSlide = new Map(edits.get(slide.part) ?? []);
  if (text === shape.originalText) forSlide.delete(shapeId);
  else forSlide.set(shapeId, text);
  if (forSlide.size === 0) edits.delete(slide.part);
  else edits.set(slide.part, forSlide);

  // Reflect it immediately, collapsed exactly as the save will collapse it, so
  // the screen and the file agree.
  const first = shape.paragraphs[0]?.runs[0];
  const updated: SlideShape = {
    ...shape,
    text,
    paragraphs: text.split('\n').map((line) => ({
      runs: [{
        text: line,
        bold: first?.bold ?? false,
        italic: first?.italic ?? false,
        underline: first?.underline ?? false,
        sizePx: first?.sizePx ?? null,
        color: first?.color ?? null,
        href: null,
      }],
      text: line,
      level: shape.paragraphs[0]?.level ?? 0,
      alignment: shape.paragraphs[0]?.alignment ?? null,
      bulleted: shape.paragraphs[0]?.bulleted ?? true,
    })),
  };

  const shapes = slide.shapes.map((s) => (s.id === shapeId ? updated : s));
  const slides = model.slides.map((s, i) => (
    i === slideIndex ? { ...s, shapes, title: titleOf(shapes) } : s
  ));

  return { ...model, slides, edits };
}

/** Whether a slide holds any pending edit, for the outline's changed marker. */
export function slideIsEdited(model: PptxModel, slideIndex: number): boolean {
  const slide = model.slides[slideIndex];
  return slide ? (model.edits.get(slide.part)?.size ?? 0) > 0 : false;
}

/** Outline text of the whole deck, for search and for copying out (§17). */
export function pptxToPlainText(model: PptxModel): string {
  return model.slides.map((slide) => {
    const lines = [`— 슬라이드 ${slide.index + 1}${slide.title ? `: ${slide.title}` : ''}`];
    for (const shape of slide.shapes) {
      if (shape.kind === 'text' && shape.text.trim()) lines.push(shape.text);
      if (shape.kind === 'frame' && shape.rows.length > 0) {
        for (const row of shape.rows) lines.push(row.join('\t'));
      }
    }
    if (slide.notes) lines.push(`[노트] ${slide.notes}`);
    return lines.join('\n');
  }).join('\n\n');
}
